import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Card from '@mui/material/Card'
import CardMedia from '@mui/material/CardMedia'
import Divider from '@mui/material/Divider'
import Box from '@mui/material/Box'
import ContentOverviewCard, {
  ContentOverviewProps,
} from './ContentOverviewCard'
import EpubViewer from '../Viewer/EpubViewer'
import { FileType } from '../Elements/FileTypeResource'
import ShowFullScreenButton, {
  sharedButtonStyles,
} from '../Buttons/ShowFullScreenButton'
import { styled } from '@mui/system'
import { useShowOnDesktop } from '../../hooks/useShowOnDesktop'
import { Button, Typography, useTheme } from '@mui/material'
import { useParams } from 'react-router'
import AddAssetToPlaylistModal from '../Modals/AddAssetToPlaylistModal'
import { FetchUserPlaylistsPlaylistTypeEnum as playlistTypeEnum } from '../../swagger'

const DividerWithMargin = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
}))

interface ContentDetailsProps extends ContentOverviewProps {
  filetype: FileType | null
  viewOnly: boolean
  embedUrl: string | undefined
  showFileByType?: string
  playlistType: playlistTypeEnum
}

const ContentDetailsCard: React.FunctionComponent<ContentDetailsProps> = ({
  filetype,
  embedUrl,
  playlistType,
  handleFavoriteButtonClicked,
  ...rest
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const isDesktop = useShowOnDesktop()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { academicYear } = useParams<{
    academicYear: string
  }>()
  const dontShowIFrame = filetype === FileType.EBook
  const showPlaylistButton =
    filetype === FileType.Audio || filetype === FileType.Video

  const cardMedia = dontShowIFrame ? (
    <Box border={1} borderColor="primary.main">
      <EpubViewer title={rest.title ?? ''} url={embedUrl ?? ''} />
    </Box>
  ) : (
    <Box display="flex" flex="1" height={!isDesktop ? 250 : 600}>
      <CardMedia
        component="iframe"
        src={embedUrl}
        allowFullScreen={true}
        title={rest.title}
      />
    </Box>
  )

  return (
    <Card>
      <Box p={{ xs: 2, sm: 4 }}>
        <ContentOverviewCard
          {...rest}
          handleFavoriteButtonClicked={handleFavoriteButtonClicked}
          mobileMedia={!isDesktop ? cardMedia : undefined}
        />
        <Box
          display="flex"
          justifyContent="flex-end"
          flexWrap="wrap"
          gap={1}
          mt={{ xs: 2 }}
        >
          {showPlaylistButton && (
            <>
              <AddAssetToPlaylistModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                assetKeys={[rest.assetKey]}
                playlistType={playlistType}
              />
              <Button
                sx={sharedButtonStyles({ theme })}
                id="addToPlaylistButton"
                onClick={() => setIsModalOpen(true)}
              >
                <Typography
                  fontWeight="bold"
                  fontFamily="sweet-sans-pro, sans-serif"
                >
                  {t(
                    'AddToPlaylistButton.Label.LaunchModal',
                    'Add to Playlist'
                  )}
                </Typography>
              </Button>
            </>
          )}
          <ShowFullScreenButton
            assetKey={rest.assetKey}
            academicYear={academicYear}
            buttonContainerStyles={{
              [theme.breakpoints.down('sm')]: {
                width: '100%',
                justifyContent: 'center',
              },
            }}
          />
        </Box>

        {isDesktop && (
          <>
            <DividerWithMargin />
            {cardMedia}
          </>
        )}
      </Box>
    </Card>
  )
}

export default ContentDetailsCard
